import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { isEqual, isRelative, parseURL, resolveURL } from "ufo";
import { clientMetaTags, RouterContext, RouterStateContext } from "../context.js";
import { $routeMetaStore } from "../hooks/useRouteMeta.js";
import { RouteLoader } from "../loader.js";
import { getLinkHandlerMode, getRouteMeta, isSamePathname, normalizeRoute, parseQuery, stringifyRouteLink, } from "../utils.js";
import { AppRenderer } from "./RouteRenderer.js";
// Create a global RouteLoader instance
const loader = new RouteLoader();
// Create a unique ID for each history entry
let historyStamp = String(new Date().getTime());
let historyIndex = 0;
function getHistoryId() {
    return historyStamp + historyIndex++;
}
// Set up the initial route data
let initialRoute;
let initialRouteHydrated = false;
if (env.client && !env.admin) {
    const initialData = window._PAGE_DATA;
    const appData = initialData.appData;
    loader.setAppData(appData.data);
    loader.populateRouteData(document.location.pathname, initialData);
    const initialURL = parseURL(document.location.href);
    initialRoute = normalizeRoute({
        id: "initial",
        component: loader.getRouteComponent(initialData.view),
        pathname: initialURL.pathname,
        hash: "",
        props: initialData.viewData?.data ?? {},
        search: "",
        query: {},
        view: initialData.view,
        meta: getRouteMeta(initialData),
    });
    history.replaceState(historyStateForRoute(initialRoute), "", document.location.href);
    $routeMetaStore.data = getRouteMeta(initialData);
}
function parseHrefPath(url) {
    if (isRelative(url)) {
        url = resolveURL(document.location.href, url);
    }
    const parsed = parseURL(url);
    return parsed.pathname;
}
function parseHref(url) {
    if (isRelative(url)) {
        url = resolveURL(document.location.href, url);
    }
    return parseURL(url);
}
function parseRouteLink(url) {
    const parsed = parseHref(url);
    return {
        pathname: parsed.pathname,
        query: parseQuery(parsed.search),
        hash: parsed.hash,
    };
}
function historyStateForRoute(route) {
    return {
        id: route.id,
        key: route.key,
        view: route.view,
        state: route.returnState,
    };
}
let clientRouter = null;
export function getClientRouter() {
    return clientRouter;
}
export function BrowserRouter(props) {
    const pendingRoute = useRef(null);
    const [transitioning, startTransition] = useTransition();
    const [routerState, setRouterState] = useState(() => ({
        activeRoute: initialRoute,
        history: [initialRoute],
        blockers: [],
    }));
    const { internals, api } = useMemo(() => {
        let subscribers = new Set();
        let state = routerState;
        const setState = (update) => {
            state = {
                ...state,
                ...update,
            };
            if (state?.activeRoute?.meta?.tags) {
                if (env.serverless) {
                    clientMetaTags.setMetaTags(state.activeRoute.meta.tags ?? []);
                }
                else {
                    const title = state.activeRoute.meta.tags.find((tag) => tag.tagName === "title")?.inner;
                    if (title)
                        document.title = title;
                }
            }
            setRouterState(state);
        };
        // If a dynamic compojnent fails to load during a page transition, we need to reload the page
        subscribers.add((event) => {
            if (event.type === "error:component-load-failed") {
                if (state?.pendingRoute) {
                    console.warn("Failed to load route component. May be a deployment mismatch.");
                    window.location.href = state.pendingRoute.uri;
                }
            }
        });
        const internals = {
            transitionEnded: null,
            updateRoute: null,
            poppedState: null,
        };
        history.scrollRestoration = "manual";
        const emitEvent = (event) => {
            subscribers.forEach((fn) => fn(event));
        };
        const captureState = () => {
            const state = {
                scrollTop: document.scrollingElement.scrollTop,
                scrollLeft: document.scrollingElement.scrollLeft,
            };
            emitEvent({ type: "capture-restorable-state", state: state });
            return state;
        };
        internals.updateRoute = (route) => {
            replaceRoute({
                ...state.activeRoute,
                ...route,
            }, false);
        };
        const historyCache = new Map();
        function setHistory(history) {
            setState({
                history,
                activeRoute: history[history.length - 1],
            });
            for (let item of history) {
                historyCache.set(item.id, item);
            }
        }
        internals.poppedState = (href, data) => {
            const stack = state.history;
            // Is the route in our history stack? (going back)
            const index = stack.findIndex((item) => item.id === data.id);
            if (index >= 0) {
                const route = stack[index];
                return doRouteTransition({
                    url: route.uri,
                    route,
                    goingBack: true,
                    history: stack.slice(0, index),
                    restoreState: route.returnState ?? data.state,
                    shouldPush: false,
                });
            }
            // Is the route in our history _cache_? (probably going forward)
            if (historyCache.has(data.id)) {
                const route = historyCache.get(data.id);
                return doRouteTransition({
                    url: route.uri,
                    route,
                    goingBack: false,
                    history: stack,
                    restoreState: route.returnState ?? data.state,
                    shouldPush: false,
                });
            }
            return doRouteTransition({
                url: href,
                goingBack: true,
                history: [],
                restoreState: data.state,
                shouldPush: false,
            });
        };
        const replaceRoute = (_route, updateBrowser = true) => {
            const route = normalizeRoute(_route);
            setHistory([...state.history.slice(0, -1), route]);
            if (updateBrowser) {
                history.replaceState(historyStateForRoute(route), "", route.uri);
            }
        };
        function getActiveRoute() {
            const stack = state.history;
            return stack[stack.length - 1];
        }
        async function doRouteTransition(args) {
            // clearPending()
            const link = parseRouteLink(args.url);
            let cancelled = false;
            const currentRoute = getActiveRoute();
            // Remember scroll position and other state on the outgoing route
            currentRoute.returnState = captureState();
            const hasRouteData = !!args.route || loader.hasRouteData(link.pathname);
            // Emit a navigation event
            emitEvent({
                type: "navigate:start",
                link,
                currentRoute: currentRoute,
                hasPreloadedData: hasRouteData,
                goingBack: !!args.goingBack,
                cancel() {
                    cancelled = true;
                },
            });
            if (cancelled)
                return;
            const data = await loader.loadRouteData(parseHrefPath(args.url)).catch((err) => {
                emitEvent({ type: "error", url: args.url, error: err, critical: true });
                return {
                    view: "_error",
                    viewType: "react",
                    appData: loader.appData,
                    viewData: {
                        data: {
                            code: 500,
                            error: err,
                        },
                    },
                };
            });
            const lazyComponent = loader.getRouteComponent(data.view);
            if (!lazyComponent) {
                console.error(`No component found for view: ${data.view}`);
            }
            const route = args.route ??
                normalizeRoute({
                    id: getHistoryId(),
                    hash: link.hash,
                    search: "",
                    query: link.query,
                    pathname: data.canonical ?? link.pathname,
                    view: data.view,
                    props: data.viewData?.data ?? {},
                    component: lazyComponent,
                    returnState: args.restoreState ?? {},
                    meta: getRouteMeta(data),
                });
            setState({
                pendingRoute: route,
            });
            emitEvent({
                type: "navigate:data-ready",
                currentRoute,
                loadedRoute: route,
                link,
            });
            emitEvent({
                type: "navigate:will-change",
                currentRoute,
                loadedRoute: route,
                link,
            });
            internals.transitionEnded = () => {
                internals.transitionEnded = null;
                // Update the URL
                // if (!returnState) {
                //   route.returnState = captureState()
                // }
                emitEvent({
                    type: "navigate:changed",
                    currentRoute: route,
                    lastRoute: currentRoute,
                    link,
                });
                $routeMetaStore.data = route.meta;
                // Update the 'Edit Page' button, if there is one
                if (route.meta.editLink) {
                    const button = document.querySelector("#wp-admin-bar-edit a");
                    if (button) {
                        button.setAttribute("href", route.meta.editLink);
                        button.innerHTML = "Edit Page";
                    }
                }
            };
            startTransition(() => {
                pendingRoute.current = route;
                setHistory([...(args.history || state.history), route]);
                if (args.shouldPush) {
                    history.pushState(historyStateForRoute(route), "", stringifyRouteLink(route));
                }
            });
        }
        const api = {
            loader: loader,
            hostname: window?._PAGE_DATA?.hostname,
            async navigate(url) {
                doRouteTransition({
                    url: url,
                    shouldPush: true,
                    goingBack: false,
                });
            },
            getState() {
                return state;
            },
            replaceHash(hash) {
                replaceRoute({
                    ...getActiveRoute(),
                    hash,
                });
            },
            replaceQuery(query) {
                replaceRoute({
                    ...getActiveRoute(),
                    query,
                    returnState: captureState(),
                });
            },
            async prefetch(url) {
                try {
                    const link = parseRouteLink(url);
                    if (loader.hasRouteData(link.pathname))
                        return;
                    emitEvent({ type: "preload:start", currentRoute: getActiveRoute(), link });
                    await loader.loadRouteData(link.pathname).then((data) => {
                        emitEvent({ type: "preload:data-ready", currentRoute: getActiveRoute(), link, data });
                    });
                }
                catch (err) {
                    emitEvent({ type: "error", error: err, critical: false });
                }
            },
            async preload(url) {
                try {
                    const link = parseRouteLink(url);
                    if (loader.hasRouteData(link.pathname))
                        return;
                    emitEvent({ type: "preload:start", currentRoute: getActiveRoute(), link });
                    const data = await loader.loadRouteData(link.pathname);
                    emitEvent({ type: "preload:data-ready", currentRoute: getActiveRoute(), link, data });
                    if (data.view) {
                        const lazyComponent = loader.getRouteComponent(data.view);
                        await lazyComponent.preload();
                        emitEvent({ type: "preload:finished", currentRoute: getActiveRoute(), link, data });
                    }
                }
                catch (err) {
                    emitEvent({ type: "error", error: err, critical: false });
                }
            },
            subscribe(fn) {
                subscribers.add(fn);
                return () => {
                    subscribers.delete(fn);
                };
            },
            handleClickEvent(e, originalHref, preferBack) {
                const { mode, href, isSameUrl } = getLinkHandlerMode(e, originalHref, state.activeRoute);
                let cancelled = false;
                if (href) {
                    emitEvent({
                        type: "clicked",
                        currentRoute: getActiveRoute(),
                        link: parseRouteLink(href),
                        mode,
                        href,
                        isSameUrl,
                        cancel() {
                            cancelled = true;
                        },
                    });
                }
                if (mode === "ignore" || cancelled) {
                    e.preventDefault();
                    return;
                }
                else if (mode === "native") {
                    return;
                }
                else if (mode === "navigate" && href) {
                    e.preventDefault();
                    if (preferBack) {
                        const lastState = state.history.length > 1 && state.history[state.history.length - 2];
                        if (lastState) {
                            const doesMatch = preferBack === "exact" ? isEqual(href, lastState.uri) : isSamePathname(href, lastState.uri);
                            if (doesMatch) {
                                history.back();
                                return;
                            }
                        }
                    }
                    api.navigate(href);
                }
            },
            emitEvent,
            restoreRoute(route) {
                const stack = state.history;
                // Is the route in our history stack? (going back)
                const index = stack.findIndex((item) => item.id === route.id);
                if (index >= 0) {
                    return doRouteTransition({
                        url: route.uri,
                        route,
                        goingBack: true,
                        history: stack.slice(0, index),
                        restoreState: route.returnState,
                        shouldPush: false,
                    });
                }
                // Is the route in our history _cache_? (probably going forward)
                if (historyCache.has(route.id)) {
                    return doRouteTransition({
                        url: route.uri,
                        route,
                        goingBack: false,
                        history: stack,
                        restoreState: route.returnState,
                        shouldPush: false,
                    });
                }
                return doRouteTransition({
                    url: route.uri,
                    goingBack: false,
                    history: stack,
                    restoreState: route.returnState,
                    shouldPush: false,
                });
            },
        };
        return {
            api,
            internals,
        };
    }, []);
    useEffect(() => {
        if (!transitioning && internals.transitionEnded) {
            internals.transitionEnded();
        }
    }, [transitioning]);
    useEffect(() => {
        if (!initialRouteHydrated && !transitioning) {
            initialRouteHydrated = true;
            // Calculate the bits we care about
            const hash = document.location.hash;
            const query = parseQuery(document.location.search);
            if (Object.keys(query).length > 0 || hash) {
                startTransition(() => {
                    internals.updateRoute?.({ query, hash });
                });
            }
        }
    }, [transitioning]);
    useEffect(() => {
        // Handle popState
        // const router = useRouterStore.getState()
        const onPopState = (e) => {
            // console.log("Popped", e.state)
            internals.poppedState?.(document.location.href, e.state ?? {});
        };
        window.addEventListener("popstate", onPopState);
        return () => {
            window.removeEventListener("popstate", onPopState);
        };
    }, []);
    useEffect(() => {
        props.onReady?.();
    }, []);
    clientRouter = {
        state: routerState,
        api,
    };
    return (_jsx(RouterContext.Provider, { value: api, children: _jsx(RouterStateContext.Provider, { value: routerState, children: _jsx(AppRenderer, {}) }) }));
}
